/**
 * Fluxos de atendimento (Fase 5): construtor + simulador de roteiros
 * (mensagem → pergunta com opções → repasse a humano/fim). A validação e a
 * simulação usam a MESMA regra pura de `packages/flows` que a API aplica ao
 * gravar (nenhuma lógica de negócio duplicada aqui).
 *
 * A execução automática sobre mensagens reais do WhatsApp NÃO está ligada
 * (decisão pendente do cliente, registrada na Fase 5): este construtor só
 * grava e simula o roteiro — ver o aviso fixo na tela.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { useState, type ReactElement } from "react";
import { Workflow } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { Badge, Button, EmptyState, PageHeader, controlClass } from "../ui/index.js";
import { useFlows, useFlowActions } from "../queries.js";
import type { SavedFlow, SavedFlowInput } from "../api/types.js";
import { validateFlow, simulateFlow, type FlowDefinition, type FlowStep, type FlowTurn } from "../../../../packages/flows/src/index.js";

const TEMPLATE_STEPS: FlowStep[] = [
  { id: "start", type: "message", text: "Olá! Em que posso ajudar?", next: "menu" },
  { id: "menu", type: "question", text: "Escolha uma opção:", options: [{ label: "Falar com atendente", keyword: "atendente", next: "handoff" }], fallbackNext: "handoff" },
  { id: "handoff", type: "handoff", text: "Vou te passar para um atendente." }
];

function emptyForm() {
  return { name: "", triggerKeyword: "", startStepId: "start", stepsJson: JSON.stringify(TEMPLATE_STEPS, null, 2) };
}

/** Lê o textarea de passos; problemas de JSON contam como erro de validação (mesma lista exibida). */
function parseSteps(stepsJson: string): { steps: FlowStep[]; errors: string[] } {
  try {
    const parsed = JSON.parse(stepsJson);
    if (!Array.isArray(parsed)) return { steps: [], errors: ["Os passos precisam ser uma lista (JSON array)."] };
    return { steps: parsed as FlowStep[], errors: [] };
  } catch {
    return { steps: [], errors: ["O JSON dos passos está malformado."] };
  }
}

function FlowForm({ editing, onDone }: { editing: SavedFlow | null; onDone: () => void }): ReactElement {
  const { save } = useFlowActions();
  const [form, setForm] = useState(() =>
    editing ? { name: editing.name, triggerKeyword: editing.triggerKeyword, startStepId: editing.startStepId, stepsJson: JSON.stringify(editing.steps, null, 2) } : emptyForm()
  );
  const [problems, setProblems] = useState<string[]>([]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const { steps, errors } = parseSteps(form.stepsJson);
    if (errors.length) { setProblems(errors); return; }
    const definition: FlowDefinition = { triggerKeyword: form.triggerKeyword.trim(), startStepId: form.startStepId.trim(), steps };
    const validationErrors = validateFlow(definition);
    if (validationErrors.length) { setProblems(validationErrors); return; }
    const input: SavedFlowInput = { ...definition, name: form.name.trim() };
    save.mutate({ input, id: editing?.id }, { onSuccess: () => { setProblems([]); onDone(); } });
  };

  return (
    <form className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" aria-label={editing ? "Editar fluxo" : "Novo fluxo"} onSubmit={submit}>
      <label className="text-sm font-extrabold text-ink" htmlFor="flow-name">Nome do fluxo</label>
      <input id="flow-name" className={controlClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} required />
      <label className="text-sm font-extrabold text-ink" htmlFor="flow-trigger">Palavra que inicia o fluxo</label>
      <input id="flow-trigger" className={controlClass} value={form.triggerKeyword} onChange={(e) => setForm({ ...form, triggerKeyword: e.target.value })} maxLength={40} required />
      <label className="text-sm font-extrabold text-ink" htmlFor="flow-start">Passo inicial (id)</label>
      <input id="flow-start" className={controlClass} value={form.startStepId} onChange={(e) => setForm({ ...form, startStepId: e.target.value })} required />
      <label className="text-sm font-extrabold text-ink" htmlFor="flow-steps">Passos (JSON)</label>
      <textarea id="flow-steps" className={controlClass + " min-h-56 py-2 font-mono text-xs"} value={form.stepsJson} onChange={(e) => setForm({ ...form, stepsJson: e.target.value })} required />
      <div className="flex justify-end gap-2">
        {editing ? <Button onClick={onDone}>Cancelar</Button> : null}
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {editing ? "Salvar" : "Criar fluxo"}
        </Button>
      </div>
      {problems.length ? (
        <ul className="m-0 flex flex-col gap-1 pl-4 text-sm text-danger" role="alert">
          {problems.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      ) : null}
      {save.error ? <p className="m-0 text-sm text-danger" role="alert">{errorDetail(save.error)}</p> : null}
    </form>
  );
}

function Simulator({ flows }: { flows: SavedFlow[] }): ReactElement | null {
  const [flowId, setFlowId] = useState("");
  const [repliesText, setRepliesText] = useState("");
  const [transcript, setTranscript] = useState<FlowTurn[] | null>(null);
  const flow = flows.find((item) => item.id === flowId);
  if (flows.length === 0) return null;
  return (
    <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" aria-label="Simulador de fluxo">
      <h3 className="m-0 text-lg font-extrabold text-ink">Simular conversa</h3>
      <p className="m-0 text-sm text-muted">Nada é enviado ao cliente: a simulação roda só nesta tela.</p>
      <label className="text-sm font-extrabold text-ink" htmlFor="sim-flow">Fluxo</label>
      <select id="sim-flow" className={controlClass} value={flowId} onChange={(e) => { setFlowId(e.target.value); setTranscript(null); }}>
        <option value="">Selecione…</option>
        {flows.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      <label className="text-sm font-extrabold text-ink" htmlFor="sim-replies">Respostas do cliente (uma por linha, na ordem)</label>
      <textarea id="sim-replies" className={controlClass + " min-h-24 py-2"} value={repliesText} onChange={(e) => setRepliesText(e.target.value)} />
      <Button
        className="self-end"
        disabled={!flow}
        onClick={() => {
          if (!flow) return;
          const replies = repliesText.split("\n").map((line) => line.trim()).filter(Boolean);
          setTranscript(simulateFlow(flow, replies).transcript);
        }}
      >
        Simular
      </Button>
      {transcript ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0" aria-label="Transcrição simulada">
          {transcript.map((turn, index) => (
            <li key={index} className={`max-w-[80%] rounded-panel px-3 py-2 text-sm ${turn.from === "bot" ? "bg-surface-soft text-ink" : "self-end bg-brand-wash text-ink"}`}>
              <span className="whitespace-pre-wrap">{turn.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function FluxosView({ flows }: { flows: SavedFlow[] }): ReactElement {
  const { remove } = useFlowActions();
  const [editing, setEditing] = useState<SavedFlow | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="page flex flex-col gap-6" data-page="fluxos">
      <PageHeader
        title="Fluxos de atendimento"
        description="Construa e simule roteiros de resposta. A execução automática sobre o WhatsApp real ainda não está ligada — use o simulador abaixo para validar o roteiro."
        actions={
          editing || creating ? null : (
            <Button variant="primary" onClick={() => setCreating(true)}>
              Novo fluxo
            </Button>
          )
        }
      />
      {editing || creating ? (
        <FlowForm editing={editing} onDone={() => { setEditing(null); setCreating(false); }} />
      ) : null}
      {flows.length === 0 ? (
        <EmptyState icon={Workflow} title="Nenhum fluxo cadastrado" description="Use o botão Novo fluxo para criar o primeiro roteiro." />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Fluxos cadastrados">
          {flows.map((flow) => (
            <li key={flow.id} className="flex flex-wrap items-center justify-between gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1" data-record-id={flow.id} data-trigger={flow.triggerKeyword}>
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-base font-extrabold text-ink">{flow.name}</span>
                <span className="flex flex-wrap items-center gap-2 text-sm text-muted">
                  <Badge tone="brand">{`gatilho "${flow.triggerKeyword}"`}</Badge>
                  {`${flow.steps.length} passo(s)`}
                </span>
              </span>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => { setCreating(false); setEditing(flow); }}>Editar</Button>
                <Button size="sm" variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(flow.id)}>Excluir</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Simulator flows={flows} />
    </div>
  );
}

export function Fluxos(): ReactElement {
  const query = useFlows();
  if (query.isPending) return <StatusPanel kind="loading" title="Carregando fluxos…" />;
  if (query.error) return <StatusPanel kind="error" title="Não foi possível carregar os fluxos" detail={errorDetail(query.error)} />;
  return <FluxosView flows={query.data ?? []} />;
}
